/**
 * The festival page's requests, pure: which mode a page is in, the bodies Approve and Publish changes send, and the few
 * problems that would keep a body from being sent at all.
 *
 * **IMS holds no preview text and no copy of Tina's validation.** What Approve or Publish changes would make, and every
 * problem Tina would answer, come from Tina's dry run. IMS checks only what FastAPI's request models refuse before
 * Tina's rules run (a blank name, no day, a day with no venue, a malformed set time or poster url): those few problems
 * keep the preview from being asked for and Approve off, with the same sentences (`pageProblemText`).
 */
import {
  type FormDay,
  type FormEntry,
  type PageForm,
} from "@/pages/tina/festivals/lib/page-form";
import type {
  ApproveDayTicketBody,
  ChangeDayIn,
  DayLineupEntry,
  ExistingChoiceIn,
  FestivalPageRead,
  PreviewBody,
  PublishChangesBody,
} from "@/types/tina-festival-page";
import type { PageChoice } from "@/types/tina-festivals";

export type PageMode =
  | "question"
  | "approve"
  | "group"
  | "changes"
  | "program_live"
  | "read_only";

/** What the page lets the curator do. */
export function pageMode(page: FestivalPageRead): PageMode {
  if (page.kind_question) return "question";
  if (page.state === "proposed") {
    if (page.kind === "day_ticket") return "approve";
    if (page.kind === "program" || page.kind === "unclear") return "group";
    return "read_only";
  }
  if (
    page.state === "confirmed" &&
    page.kind === "day_ticket" &&
    page.page_version !== null
  ) {
    return "changes";
  }
  // Any confirmed or grouped festival that is not a day ticket (a program, a tour) is a group of concerts.
  if (
    (page.state === "confirmed" || page.state === "auto_grouped") &&
    page.kind !== "day_ticket"
  ) {
    return "program_live";
  }
  return "read_only";
}

/** The page's main button; an unpublished festival approves again, as a first approval does. */
export function mainButton(
  page: FestivalPageRead,
): "Approve and publish" | "Publish changes" | null {
  const mode = pageMode(page);
  if (mode === "approve") return "Approve and publish";
  if (mode === "changes") {
    return page.unpublished ? "Approve and publish" : "Publish changes";
  }
  return null;
}

/** How often the page reloads itself: every 15 s while Tina publishes, never otherwise. */
export function refetchEvery(
  page: FestivalPageRead | undefined,
): number | false {
  return page?.status === "publishing" ? 15_000 : false;
}

// ── What would make a body unsendable ───────────────────────────────────────

const SET_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

const isWebAddress = (value: string): boolean => {
  try {
    const { protocol } = new URL(value);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
};

const byDate = (a: FormDay, b: FormDay): number =>
  a.day < b.day ? -1 : a.day > b.day ? 1 : 0;

const namedEntries = (day: FormDay): FormEntry[] =>
  day.lineup.filter((entry) => entry.name.trim() !== "");

/**
 * Only what the request model would refuse, in this order: `blank_name`, `no_days`, `venue_missing:<day>` (a box with
 * no venue id, or with an id and no name: the model needs both, so the curator picks it again), `bad_set_time:<day>`
 * and `bad_poster_url`. Every other problem is Tina's, from the preview.
 */
export function shapeProblems(form: PageForm): string[] {
  const problems: string[] = [];
  if (!/[\p{L}\p{N}]/u.test(form.name)) problems.push("blank_name");
  if (form.days.length === 0) problems.push("no_days");
  const days = [...form.days].sort(byDate);
  const add = (problem: string) => {
    if (!problems.includes(problem)) problems.push(problem);
  };
  for (const day of days) {
    if ((day.venueId ?? "").trim() === "" || day.venueName.trim() === "") {
      add(`venue_missing:${day.day}`);
    }
  }
  for (const day of days) {
    const bad = namedEntries(day).some(
      (entry) =>
        entry.setTime.trim() !== "" && !SET_TIME.test(entry.setTime.trim()),
    );
    if (bad) add(`bad_set_time:${day.day}`);
  }
  const poster = form.posterUrl.trim();
  if (poster !== "" && !isWebAddress(poster)) problems.push("bad_poster_url");
  return problems;
}

// ── Bodies ──────────────────────────────────────────────────────────────────

const orNull = (value: string): string | null =>
  value.trim() === "" ? null : value.trim();

function lineupBody(day: FormDay): DayLineupEntry[] {
  return namedEntries(day).map((entry) => ({
    name: entry.name.trim(),
    artist_id: entry.artistId,
    set_time: orNull(entry.setTime),
    stage: orNull(entry.stage),
    photo_url: entry.artistId === null ? null : entry.photoUrl,
  }));
}

/**
 * Every choice for an event already in Mood. A "Not part" names no day; a "Replace" folds into the day it names while
 * that is a page day, else into the first day (the screen shows no day for it); a "Use as Day" names its day as it is,
 * for Tina to judge.
 */
function existingBody(form: PageForm): ExistingChoiceIn[] {
  const dates = new Set(form.days.map((d) => d.day));
  const first = [...form.days].sort(byDate)[0]?.day ?? null;
  return Object.entries(form.existing).map(([id, line]) => {
    if (line.choice === "not_part") {
      return { mood_event_id: id, choice: line.choice, day: null };
    }
    if (line.choice === "replace") {
      const day = line.day !== null && dates.has(line.day) ? line.day : first;
      return { mood_event_id: id, choice: line.choice, day };
    }
    return { mood_event_id: id, choice: line.choice, day: line.day };
  });
}

/** Every page with its two boxes; the own-page mark is sent only on a member page. */
function pagesBody(form: PageForm): PageChoice[] {
  return Object.entries(form.pages).map(([url, page]) => ({
    url,
    member: page.member,
    own_page: page.member && page.ownPage,
  }));
}

const aliasesBody = (form: PageForm): string[] => [
  ...new Set(form.aliases.map((a) => a.trim()).filter((a) => a !== "")),
];

/** The body of "Approve and publish" before approval. */
export function toApproveBody(
  form: PageForm,
  page: FestivalPageRead,
): ApproveDayTicketBody {
  return {
    canonical_name: form.name.trim(),
    aliases: aliasesBody(form),
    days: [...form.days].sort(byDate).map((day) => ({
      day: day.day,
      venue_id: day.venueId ?? "",
      venue_name: day.venueName.trim(),
      lineup: lineupBody(day),
    })),
    existing: existingBody(form),
    pages: pagesBody(form),
    poster_url: orNull(form.posterUrl),
    skeleton_digest: page.skeleton_digest ?? "",
  };
}

/** The body of "Publish changes" (and of "Approve and publish" again after an unpublish). */
export function toPublishChangesBody(
  form: PageForm,
  page: FestivalPageRead,
): PublishChangesBody {
  return {
    canonical_name: form.name.trim(),
    aliases: aliasesBody(form),
    days: [...form.days].sort(byDate).map(
      (day): ChangeDayIn => ({
        day: day.day,
        from_day:
          day.loadedDay !== null && day.loadedDay !== day.day
            ? day.loadedDay
            : null,
        venue_id: day.venueId ?? "",
        venue_name: day.venueName.trim(),
        lineup: lineupBody(day),
        cancelled: day.cancelled,
      }),
    ),
    removed: Object.entries(form.removed).map(([day, removed]) => ({
      day,
      delete_in_mood: removed.deleteInMood,
    })),
    existing: existingBody(form),
    pages: pagesBody(form),
    tray_dropped: form.trayDropped,
    page_version: page.page_version ?? "",
  };
}

/**
 * The body the dry run is asked about: the one Approve or Publish changes would send, or null while the body cannot be
 * sent (a shape problem, a page with nothing to approve, a record with no skeleton to approve against).
 */
export function previewBody(
  form: PageForm,
  page: FestivalPageRead,
): PreviewBody | null {
  if (shapeProblems(form).length > 0) return null;
  switch (pageMode(page)) {
    case "approve":
      return page.skeleton_digest === null ? null : toApproveBody(form, page);
    case "changes":
      return toPublishChangesBody(form, page);
    default:
      return null;
  }
}

// ── The tray ────────────────────────────────────────────────────────────────

const plural = (count: number, noun: string): string =>
  count === 1 ? `${count} ${noun}` : `${count} ${noun}s`;

/** The line over the tray: how many artists the page lists without a day, and how many are placed already. */
export function trayHint(trayCount: number, placedCount: number): string {
  if (trayCount <= 0) return "";
  const total = trayCount + placedCount;
  const lead = `The page lists ${plural(total, "artist")} but not the day ${total === 1 ? "it plays" : "each one plays"}.`;
  if (placedCount > 0) {
    return `${lead} ${placedCount} ${placedCount === 1 ? "is" : "are"} placed; drag the other ${trayCount} to a day.`;
  }
  return `${lead} Drag ${trayCount === 1 ? "it" : "each one"} to a day.`;
}

const TRAY_PREFIX = "tray:";
const DAY_PREFIX = "day:";

/** The id a tray chip is dragged by. */
export const trayDragId = (name: string): string => `${TRAY_PREFIX}${name}`;

/** The artist name a drag id carries when it is a tray chip, else null. */
export function trayDragName(id: string | number | undefined): string | null {
  if (typeof id !== "string" || !id.startsWith(TRAY_PREFIX)) return null;
  return id.slice(TRAY_PREFIX.length);
}

/** The id a day box accepts a drop by. */
export const dayDropId = (key: string): string => `${DAY_PREFIX}${key}`;

/** A tray chip dropped on a day box as a placement, or null for any other drop. */
export function trayDropTarget(event: {
  active: { id: string | number };
  over: { id: string | number } | null;
}): { name: string; key: string } | null {
  const { active, over } = event;
  if (over === null) return null;
  const name = trayDragName(active.id);
  if (name === null || typeof over.id !== "string") return null;
  if (!over.id.startsWith(DAY_PREFIX)) return null;
  return { name, key: over.id.slice(DAY_PREFIX.length) };
}
